import { Button, Divider, LinearProgress, Stack } from "@mui/material";
import React, { useContext, useState } from "react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { FormattedMessage, useIntl } from "react-intl";
import { theme } from "../../../styles/themeProvider";
import { UserApi } from "../../../api/userApi";
import {
	BusinessDataContext,
	LoginContext,
	LoginContextValues,
	MultistepContext,
	MultistepContextValues,
} from "../../../types/context";
import GoogleLogin from "./GoogleLogin";
import { ErrorTypography, FormField } from "../../styled/formComponents";
import { RegisterButton } from "../../buttons/RegisterButton";
import { findComponentStep } from "../../../utils/filter";
import { tryCatchHandler } from "../../../utils/tryCatchHandler";

interface ChangePasswordValues {
	password: string;
	confirmPassword: string;
}

const ChangePassword = ({ setSuccess }) => {
	const { reservation, onChangeLogin, onChangeStep } = useContext(
		MultistepContext
	) as MultistepContextValues;

	const businessDataContext = useContext(BusinessDataContext);

	const { onLogin, isCalledFromProfile } = useContext(
		LoginContext
	) as LoginContextValues;

	const { formatMessage } = useIntl();
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);

	const initialValues = {
		password: "",
		confirmPassword: "",
	};

	const requiredMessage = formatMessage({
		id: "error.required",
		defaultMessage: "Campo requerido.",
	});

	const changePasswordSchema = Yup.object().shape({
		password: Yup.string()
			.required(requiredMessage)
			.min(
				8,
				formatMessage({
					id: "error.password",
					defaultMessage: "La contraseña debe tener al menos 8 caracteres.",
				})
			),
		confirmPassword: Yup.string()
			.oneOf(
				[Yup.ref("password"), undefined],
				formatMessage({
					id: "error.confirmPassword",
					defaultMessage: "Las contraseñas deben coincidir.",
				})
			)
			.required(requiredMessage),
	});

	const handleLogin = async (values: ChangePasswordValues) => {
		setError("");
		setLoading(true);
		const userApi = new UserApi();

		const errorMessage = await tryCatchHandler(
			async () => {
				await userApi.updatePassword(reservation.customer.id!, values.password);

				setSuccess(
					formatMessage({
						id: "changePassword.success",
						defaultMessage: "Contraseña actualizada con éxito.",
					})
				);

				onLogin();

				if (isCalledFromProfile) {
					const nextStep = findComponentStep(
						businessDataContext!.selectedFlow!,
						"Profile"
					);
					onChangeStep(nextStep);
				} else {
					const nextStep = findComponentStep(
						businessDataContext!.selectedFlow!,
						"Details"
					);
					onChangeStep(nextStep);
				}
				
				const response = await userApi.login(
					reservation.customer.email,
					values.password
				);
				onChangeLogin(response.data);
				setLoading(false);
			},
			"changePassword.error",
			"Ocurrió un error al actualizar la contraseña.",
			formatMessage
		);
		if (errorMessage) {
			setError(errorMessage);
			setLoading(false);
		}
	};

	return (
		<Stack
			sx={{ width: { mobile: "85%", xs: "75%", sm: "70%", md: "65%" } }}
			paddingBottom={reservation.customer ? "16px" : "0px"}
		>
			<Formik
				initialValues={initialValues}
				validationSchema={changePasswordSchema}
				onSubmit={async (values) => await handleLogin(values)}
			>
				{({
					values,
					errors,
					touched,
					handleChange,
					handleBlur,
					handleSubmit,
					isSubmitting,
				}) => (
					<Form style={{ width: "100%", alignItems: "center" }}>
						<Stack spacing={2}>
							<FormField
								name="password"
								label="Contraseña"
								type="password"
								value={values.password}
								touched={touched.password}
								error={errors.password}
								handleBlur={handleBlur}
								handleChange={handleChange}
							/>

							<FormField
								name="confirmPassword"
								label="Confirmar contraseña"
								type="password"
								value={values.confirmPassword}
								touched={touched.confirmPassword}
								error={errors.confirmPassword}
								handleBlur={handleBlur}
								handleChange={handleChange}
							/>

							{error && <ErrorTypography>{error}</ErrorTypography>}
							{loading && <LinearProgress />}
						</Stack>
						<Button
							fullWidth
							type="submit"
							variant="contained"
							disabled={isSubmitting}
							sx={{ height: 50, mt: 4 }}
							onClick={(event) => {
								event.preventDefault();
								handleSubmit();
							}}
						>
							<FormattedMessage
								id="changePassword.enter"
								defaultMessage="Confirmar"
							/>
						</Button>
					</Form>
				)}
			</Formik>
		</Stack>
	);
};

export default ChangePassword;
